import moment from "moment";
import DocumentService from "@/services/documentService";

export const DocumentsStore = {
    namespaced: true,
    state: {
        tabs: {
            downloads: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "created_at",
                sortOrder: -1,
                first: 0,
                enableExpander: false,
                expandedRows: [],
            },
            bulletins: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "created_at",
                sortOrder: -1,
                first: 0,
                enableExpander: false,
                expandedRows: [],
            },
            all: {
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "created_at",
                sortOrder: -1,
                first: 0,
                enableExpander: false,
                expandedRows: [],
            },
        },

        currentTab: "downloads",
        searchText: "",
        fromDate: null,
        toDate: null,
        category: "",
        visibility: "",
        documentType: "",
        docDate: null,
        accessLevel: "",
        isLoading: false,
        lastSelectedRowId: "",
    },

    mutations: {
        SET_TABLE_DATA(state, { tab, data }) {
            state.tabs[tab].tableData = data.data;
            state.tabs[tab].totalRecords = data.count;
        },

        UPDATE_CURRENT_PAGE(state, { tab, page }) {
            state.tabs[tab].currentPage = page;
        },

        UPDATE_ROWS_PER_PAGE(state, { tab, rows }) {
            state.tabs[tab].rowsPerPage = rows;
        },

        UPDATE_SORT_OPTIONS(state, { tab, field, order }) {
            state.tabs[tab].sortField = field;
            state.tabs[tab].sortOrder = order;
        },

        RESET_FIRST_VALUE(state, { tab }) {
            const currentPage = state.tabs[tab].currentPage;
            const rowsPerPage = state.tabs[tab].rowsPerPage;
            state.tabs[tab].first = currentPage > 0 ? (currentPage - 1) * rowsPerPage : 0;
        },

        RESET_CURRENT_PAGE(state, { tab, page = 0 }) {
            state.tabs[tab].currentPage = page;
        },

        UPDATE_CURRENT_TAB(state, currentTab) {
            state.currentTab = currentTab;
        },

        UPDATE_SEARCH_TEXT(state, searchText) {
            state.searchText = searchText;
        },

        UPDATE_FROM_DATE(state, fromDate) {
            state.fromDate = fromDate;
        },

        UPDATE_TO_DATE(state, toDate) {
            state.toDate = toDate;
        },

        UPDATE_CATEGORY(state, category) {
            state.category = category;
        },

        UPDATE_VISIBILITY(state, visibility) {
            state.visibility = visibility;
        },

        UPDATE_DOCUMENT_TYPE(state, documentType) {
            state.documentType = documentType;
        },

        UPDATE_DOC_DATE(state, docDate) {
            state.docDate = docDate;
        },

        UPDATE_ACCESS_LEVEL(state, accessLevel) {
            state.accessLevel = accessLevel;
        },

        SET_LOADING(state, isLoading) {
            state.isLoading = isLoading;
        },

        SET_LAST_SELECTED_ROW(state, lastSelectedRowId) {
            state.lastSelectedRowId = lastSelectedRowId;
        },

        SET_EXPANDED_ROWS(state, { tab, rows }) {
            state.tabs[tab].expandedRows = rows;
        },
    },

    actions: {
        async fetchTableData({ commit, state, dispatch }) {
            commit("SET_LOADING", true);

            const currentTabState = state.tabs[state.currentTab];
            const params = buildParams(currentTabState, state);
            const apiEndpoint = getApiEndpoint(state.currentTab);
            const queryString = buildQueryString(params);

            try {
                const data = await apiEndpoint(`?${queryString}`);
                commit("SET_TABLE_DATA", { tab: state.currentTab, data });
            } catch (error) {
                commit("SET_TABLE_DATA", { tab: state.currentTab, data: { data: [], count: 0 } });
                console.error("Error fetching documents:", error);
            } finally {
                commit("SET_LOADING", false);
                dispatch("resetFirstValue");
            }
        },

        updateTableSettings({ commit, dispatch, state }, { rows, page }) {
            commit("UPDATE_ROWS_PER_PAGE", { tab: state.currentTab, rows });
            commit("UPDATE_CURRENT_PAGE", { tab: state.currentTab, page: page + 1 });
            dispatch("fetchTableData");
        },

        updateSortOptions({ commit, dispatch, state }, { field, order }) {
            commit("UPDATE_SORT_OPTIONS", { tab: state.currentTab, field, order });
            dispatch("fetchTableData");
        },

        updateTab({ commit, dispatch }, currentTab) {
            commit("UPDATE_CURRENT_TAB", currentTab);
            dispatch("fetchTableData");
        },

        resetFirstValue({ commit, state }) {
            commit("RESET_FIRST_VALUE", { tab: state.currentTab });
        },

        resetCurrentPage({ commit, state }, page = 0) {
            commit("RESET_CURRENT_PAGE", { tab: state.currentTab, page });
        },

        updateSearchText({ commit }, searchText) {
            commit("UPDATE_SEARCH_TEXT", searchText);
        },

        updateDateRange({ commit }, { fromDate, toDate }) {
            commit("UPDATE_FROM_DATE", fromDate);
            commit("UPDATE_TO_DATE", toDate);
        },

        updateFilters({ commit }, filters) {
            if (filters.category !== undefined) {
                commit("UPDATE_CATEGORY", filters.category);
            }
            if (filters.visibility !== undefined) {
                commit("UPDATE_VISIBILITY", filters.visibility);
            }
            if (filters.documentType !== undefined) {
                commit("UPDATE_DOCUMENT_TYPE", filters.documentType);
            }
            if (filters.docDate !== undefined) {
                commit("UPDATE_DOC_DATE", filters.docDate);
            }
            if (filters.accessLevel !== undefined) {
                commit("UPDATE_ACCESS_LEVEL", filters.accessLevel);
            }
        },

        triggerSearch({ dispatch, commit, state }) {
            commit("RESET_CURRENT_PAGE", { tab: state.currentTab, page: 0 });
            dispatch("fetchTableData");
        },
    },
};

function buildQueryString(params) {
    return Object.entries(params)
        .filter(([k, v]) => v !== undefined && v !== null && v !== "")
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                return `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`;
            }
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join("&");
}

function buildParams(currentTabState, state) {
    const params = {
        page: currentTabState.currentPage,
        limit: currentTabState.rowsPerPage,
        orderBy: currentTabState.sortField,
        order: currentTabState.sortOrder,
    };

    if (state.searchText) {
        params.searchTerm = state.searchText;
    }

    if (state.fromDate) {
        params.fromDate = moment(state.fromDate).format("DD/MM/YYYY");
    }

    if (state.toDate) {
        params.toDate = moment(state.toDate).format("DD/MM/YYYY");
    }

    if (state.category) {
        params.category = state.category;
    }

    if (state.visibility) {
        params.visibility = state.visibility;
    }

    if (state.currentTab === "downloads") params.documentType = "download";
    if (state.currentTab === "bulletins") params.documentType = "bulletin";


    if (state.accessLevel && state.currentTab === "downloads") {
        params.accessLevel = state.accessLevel;
    }

    if (state.docDate && state.currentTab === "bulletins") {
        params.docDate = moment(state.docDate).format("DD/MM/YYYY");
    }

    return params;
}

function getApiEndpoint(currentTab) {
    return DocumentService.searchDocumentsByQuery;
}
